/**
 * Auto-Save Hook
 *
 * Debounced auto-save for draft submissions.
 * Triggers a refresh of the submission data to update last_edited_at
 * and ensure the UI stays in sync with the server.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { manualEntryApi } from '@/services/api/manualEntryApi';
import { submissionKeys } from './useManualSubmission';
import { toastError } from '@/utils/toastHelpers';

interface UseAutoSaveOptions {
  submissionId: number | undefined;
  enabled?: boolean;
  debounceMs?: number;
}

interface UseAutoSaveReturn {
  triggerSave: () => void;
  isSaving: boolean;
  lastSavedAt: Date | null;
}

export function useAutoSave({
  submissionId,
  enabled = true,
  debounceMs = 2000,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const queryClient = useQueryClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedAtRef = useRef<Date | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!submissionId) return;
      // Touch the submission to sync data with server
      await manualEntryApi.touchSubmission(String(submissionId));
    },
    onSuccess: () => {
      if (submissionId) {
        lastSavedAtRef.current = new Date();
        queryClient.invalidateQueries({
          queryKey: submissionKeys.detail(String(submissionId)),
        });
      }
    },
    onError: error => {
      console.error('Auto-save failed:', error);
      toastError.generic('Failed to sync with server');
    },
  });

  const triggerSave = useCallback(() => {
    if (!enabled || !submissionId) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new debounced save
    timeoutRef.current = setTimeout(() => {
      mutation.mutate();
    }, debounceMs);
  }, [enabled, submissionId, debounceMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    triggerSave,
    isSaving: mutation.isPending,
    lastSavedAt: lastSavedAtRef.current,
  };
}

export default useAutoSave;
